/**
 * ThemeContext — Injects the admin-configured CSS variables into :root.
 * Reads `config.theme` (active_preset_id + presets list) and sets the variables.
 * On admin changes, AdminDashboard updates config.theme via PUT /api/config; the
 * landing page re-fetches config on mount, so visitors always see the latest theme.
 */
import { createContext, useContext, useEffect, useMemo } from "react";

const ThemeContext = createContext({ theme: null, active: null });

const DEFAULT_PRESET = {
  id: "default_bw",
  name: "Default (B&W)",
  bg: "#050505",
  surface: "#121212",
  border: "rgba(255,255,255,0.1)",
  accent_primary: "#FFFFFF",
  accent_secondary: "#A3A3A3",
  text: "#FFFFFF",
  text_muted: "#A1A1AA",
};

function hexToRgb(hex) {
  if (!hex) return null;
  const m = hex.trim().match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return null;
  return `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}`;
}

export function applyTheme(preset) {
  if (typeof document === "undefined") return;
  const p = { ...DEFAULT_PRESET, ...(preset || {}) };
  const root = document.documentElement;
  root.style.setProperty("--theme-bg", p.bg);
  root.style.setProperty("--theme-surface", p.surface);
  root.style.setProperty("--theme-border", p.border);
  root.style.setProperty("--theme-accent-primary", p.accent_primary);
  root.style.setProperty("--theme-accent-secondary", p.accent_secondary);
  root.style.setProperty("--theme-text", p.text);
  root.style.setProperty("--theme-text-muted", p.text_muted);
  // RGB triplets for rgba() utilities
  const pRgb = hexToRgb(p.accent_primary);
  const sRgb = hexToRgb(p.accent_secondary);
  if (pRgb) root.style.setProperty("--theme-accent-primary-rgb", pRgb);
  if (sRgb) root.style.setProperty("--theme-accent-secondary-rgb", sRgb);
}

export function ThemeProvider({ theme, children }) {
  const active = useMemo(() => {
    if (!theme) return DEFAULT_PRESET;
    const list = theme.presets || [];
    return (
      list.find((p) => p.id === theme.active_preset_id) ||
      list[0] ||
      DEFAULT_PRESET
    );
  }, [theme]);

  useEffect(() => {
    applyTheme(active);
  }, [active]);

  return (
    <ThemeContext.Provider value={{ theme, active }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
